import { Link } from "@/lib/router-compat";
import { Check, ArrowRight, MapPin, Truck, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import FadeIn from "@/components/FadeIn";
import SEOHead from "@/components/SEOHead";
import FinalCTA from "@/components/home/FinalCTA";
import { useQuoteModal } from "@/hooks/useQuoteModal";
import { corridorBreadcrumb } from "@/lib/schema";
import PageFAQ, { faqSchema, type FAQItem } from "@/components/PageFAQ";

interface CorridorPageProps {
  seoTitle: string;
  seoDesc: string;
  canonical: string;
  h1: string;
  subtitle: string;
  corridorName: string;
  overview: string;
  keyLanes: { origin: string; dest: string; transit: string }[];
  advantages: string[];
  services: string[];
  stats: { val: string; label: string }[];
  faqs?: FAQItem[];
}

const CorridorPageTemplate = ({
  seoTitle, seoDesc, canonical, h1, subtitle, corridorName,
  overview, keyLanes, advantages, services, stats, faqs,
}: CorridorPageProps) => {
  const { open } = useQuoteModal();

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${corridorName} Freight Shipping`,
    "provider": {
      "@type": "Organization",
      "name": "Varstar Alliance",
      "telephone": "+18552267123",
    },
    "areaServed": corridorName,
    "description": seoDesc,
    "url": `https://varstaralliance.com${canonical}`,
  };

  return (
    <>
      <SEOHead title={seoTitle} description={seoDesc} canonical={canonical}>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(corridorBreadcrumb(corridorName, canonical))}</script>
        {faqs && <script type="application/ld+json">{JSON.stringify(faqSchema(faqs))}</script>}
      </SEOHead>

      {/* Hero */}
      <section className="bg-foreground pt-32 pb-20 noise-texture relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-72 h-48 bg-electric/10 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 text-center max-w-3xl relative z-10">
          <FadeIn>
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/[0.06] border border-white/[0.08]">
              <MapPin className="w-4 h-4 text-electric" />
              <span className="text-sm text-white/70 font-semibold">{corridorName}</span>
            </div>
            <h1 className="secondary-page-title text-white mb-6">{h1}</h1>
            <p className="text-lg text-white/70 mb-8">{subtitle}</p>
            <Button
              className="bg-electric text-white hover:bg-electric-glow font-semibold px-8 py-6 text-base rounded-full shadow-[0_0_30px_-5px_hsl(var(--electric)/0.4)] group"
              onClick={() => open({ ctaSource: `corridor_${corridorName.toLowerCase().replace(/[^a-z0-9]+/g, "_")}` })}
            >
              Get a {corridorName} Quote
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-card border-b border-border py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-10 lg:gap-16">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-black text-electric">{s.val}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1 font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <FadeIn>
            <h2 className="section-title text-foreground mb-6">
              {corridorName} Freight Overview
            </h2>
            <div className="text-muted-foreground text-[16px] leading-relaxed space-y-4">
              {overview.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Key Lanes */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4 max-w-4xl">
          <FadeIn>
            <h2 className="compact-heading text-foreground mb-8">Key Lanes We Cover</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {keyLanes.map((lane, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="border border-border rounded-xl p-5 flex items-center gap-4 hover:shadow-md transition-shadow bg-background">
                  <Truck className="w-5 h-5 text-electric flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-foreground text-sm">{lane.origin} → {lane.dest}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Est. transit: {lane.transit}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Services + Advantages */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <FadeIn>
              <h2 className="compact-heading text-foreground mb-6">Why Varstar for {corridorName}</h2>
              <div className="space-y-3">
                {advantages.map((a, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-electric flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground text-[15px]">{a}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="compact-heading text-foreground mb-6">Available Services</h2>
              <div className="space-y-3">
                {services.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric flex-shrink-0 mt-2" />
                    <span className="text-muted-foreground text-[15px]">{s}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {faqs && <PageFAQ title={`${corridorName} freight FAQs`} faqs={faqs} />}
      <FinalCTA />
    </>
  );
};

export default CorridorPageTemplate;
